import { Button } from "@/components/ui/button";
import { Square, MoveUpRight, LineSquiggle, Check, X } from "lucide-react";

interface DrawingToolbarProps {
  selectedTool: 'box' | 'arrow' | 'freedraw';
  onToolChange: (tool: 'box' | 'arrow' | 'freedraw') => void;
  onConfirm: () => void;
  onCancel: () => void;
  hasDrawing: boolean;
  accentColor: string;
  isFullscreen?: boolean;
}

export function DrawingToolbar({
  selectedTool,
  onToolChange,
  onConfirm,
  onCancel,
  hasDrawing,
  accentColor,
  isFullscreen = false
}: DrawingToolbarProps) {
  return (
    <div 
      className={`absolute left-1/2 transform -translate-x-1/2 z-50 flex items-center gap-1 px-2 py-1.5 bg-popover/90 border border-border/60 rounded-md shadow-2xl backdrop-blur-xl ${
        isFullscreen ? 'bottom-20' : 'bottom-6'
      }`}
      data-testid="drawing-toolbar"
    >
      <div className={`flex items-center gap-0.5 ${hasDrawing ? 'pr-1.5 border-r border-border/40' : ''}`}>
        <Button
          variant="ghost"
          size="sm"
          className={`h-7 w-7 p-0 rounded-sm transition-colors duration-100 ${selectedTool === 'box' ? 'bg-accent/80 text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-accent/60'}`}
          onPointerDown={(e) => {
            e.stopPropagation();
            onToolChange('box');
          }}
          title="Box tool"
          data-testid="tool-box"
          style={selectedTool === 'box' ? { color: accentColor } : {}}
        >
          <Square className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={`h-7 w-7 p-0 rounded-sm transition-colors duration-100 ${selectedTool === 'arrow' ? 'bg-accent/80 text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-accent/60'}`}
          onPointerDown={(e) => {
            e.stopPropagation();
            onToolChange('arrow');
          }}
          title="Arrow tool"
          data-testid="tool-arrow"
          style={selectedTool === 'arrow' ? { color: accentColor } : {}}
        >
          <MoveUpRight className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={`h-7 w-7 p-0 rounded-sm transition-colors duration-100 ${selectedTool === 'freedraw' ? 'bg-accent/80 text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-accent/60'}`}
          onPointerDown={(e) => {
            e.stopPropagation();
            onToolChange('freedraw');
          }}
          title="Free draw tool"
          data-testid="tool-freedraw"
          style={selectedTool === 'freedraw' ? { color: accentColor } : {}}
        >
          <LineSquiggle className="h-3.5 w-3.5" />
        </Button>
      </div>

      {hasDrawing && (
        <div className="flex items-center gap-0.5 pl-1.5 border-l border-border/40">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 rounded-sm text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors duration-100"
            onPointerDown={(e) => {
              e.stopPropagation();
              onConfirm();
            }}
            title="Confirm drawing"
            data-testid="button-confirm-drawing"
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 rounded-sm text-red-500 hover:text-red-400 hover:bg-red-500/10 transition-colors duration-100"
            onPointerDown={(e) => {
              e.stopPropagation();
              onCancel();
            }}
            title="Cancel drawing"
            data-testid="button-cancel-drawing"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
